import { Fragment, Suspense, lazy, memo, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  AlertTriangle,
  Bot,
  ChevronDown,
  ChevronRight,
  Maximize2,
  Sparkles,
  Split
} from 'lucide-react'
import { ChatMarkdown } from '../ChatMarkdown'
import { Button } from '../ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'
import type {
  AgentOrchestratorPlanStep,
  AgentOrchestratorStepResult,
  ConversationRun,
  ConversationAttachment,
  InFlightIngestWait,
  LiveOrchestratorToolCall,
  LiveSegment,
  ModelFallbackInfo,
  ContextCompactedInfo
} from '@axilon-apps/domains-ontology-ui-state'
import { flattenMessagesToToolCalls } from '../ToolTrace/MessageList'
import { synthesizeFlatRunPlan } from './flatRunPlan'
import {
  describeCustomAgentFollowUpOutcome,
  parseChatWakeupInstruction,
  SWITCH_TO_AGENT_MODE_TOOL_NAME,
  type CustomAgentFollowUpOutcome,
  type TurnPhaseTiming
} from '@atbash-labs/ontology-agent-wire'
import {
  readCallDurationMs,
  readCallStartedAt,
  readCallTurnTiming
} from '../ToolTrace/toolCallTiming'
import {
  deriveChatStatus,
  statusLineFor,
  useRotatingThinkingVerb,
  type ChatStatus
} from '../../lib/chat-status'
import { useDebugMode } from '../../lib/debug-mode'
import {
  formatContextCompactedNotice,
  formatModelFallbackNotice
} from '../../lib/model-policy-labels'
import { formatCompactLocalTime } from '../../lib/time'
import { cn } from '../../lib/utils'
import { agentLabel } from '../../lib/agentLabels'
import {
  aggregateStepEntityLinks,
  chatArtifactEntityLinks,
  LIST_ROUTE_FOR,
  type EntityLink,
  type EntityLinkKind
} from './entityLinks'
import { collectChatArtifactRefs, type ChatArtifactRef } from './chatArtifacts'
import {
  aggregateStepIngestSubgraphs,
  sanitizeSubgraphSvg,
  type IngestSubgraphPreview
} from './ingestSubgraphs'
import {
  extractAgentModeSwitch,
  extractStreamingAnswer,
  hasCompleteAnswerEnvelope,
  stripAnswerEnvelope
} from './answerEnvelope'
import { pastTurnCot } from './cotSegments'
import { CopyButton } from './CopyButton'
import { LiveTranscript } from './LiveTranscript'
import {
  countReasoningLookups,
  elapsedSecondsBetween,
  formatReasoningSummary,
  isIgnoredReasoningTool
} from './reasoning-summary'
import { parseResumeInstruction, ResumeDecisionsBubble } from './ResumeDecisionsBubble'
import { SidebarToolCallList, type SidebarToolCallEntry } from './SidebarToolCall'
import { summarizeToolBatches } from './toolCallBatches'
import { ArtifactPreviewCard } from './ArtifactPreviewCard'
import { CompletedCustomAgentRunLinks } from './CompletedCustomAgentRunLinks'
import { handoffChipText, splitHandoffChip } from './handoff-chip'
import { compactionInfoFromRun, formatDebugCompactionNotice } from './compaction-from-run'

const BulkAgentBatchOutcomeCard = lazy(() =>
  import('./BulkAgentBatchCards').then((module) => ({
    default: module.BulkAgentBatchOutcomeCard
  }))
)

const FORK_FROM_HERE_LABEL = 'Start a new chat from this location'

function ForkFromHereButton({ onFork, disabled }: { onFork: () => void; disabled?: boolean }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={FORK_FROM_HERE_LABEL}
            title={FORK_FROM_HERE_LABEL}
            disabled={disabled}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onFork()
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            <Split className="size-3.5" />
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom">{FORK_FROM_HERE_LABEL}</TooltipContent>
    </Tooltip>
  )
}

const ENTITY_KIND_LABEL: Record<EntityLinkKind, string> = {
  notification_rule: 'notification rule',
  custom_agent_config: 'custom agent',
  chat_artifact: 'artifact'
}

const MAX_INLINE_ENTITY_LINKS = 3

interface ChatTurnPropsLive {
  kind: 'live'
  prompt: string
  /** `null` while the planner is still thinking. */
  plan: AgentOrchestratorPlanStep[] | null
  stepResults: Record<number, AgentOrchestratorStepResult>
  toolCalls: LiveOrchestratorToolCall[]
  /**
   * Per-step inline-transcript committed segments — finished progress notes
   * from the subagent's LLM. Driven by
   * `agent_orchestrator_subagent_progress_commit` events. Past turns
   * loaded in this session carry the in-memory copy stamped by
   * `appendTabTurn`; refresh / server-loaded turns are empty until
   * durable persistence ships.
   */
  liveSegmentsByStep: Record<number, LiveSegment[]>
  /**
   * Per-step in-flight pending text — the current text content block being
   * streamed token-by-token by the subagent's LLM. Promoted into a
   * committed segment on non-finalize tool_use; discarded on finalize so
   * the answer is not duplicated under the CoT dropdown and the chat body.
   */
  pendingProgressByStep: Record<number, string>
  /**
   * Per-step orchestrator inter-step narration — the planner LLM's
   * connective prose between subagent dispatches, keyed by the step
   * about to be dispatched. Streamed token-by-token via
   * `agent_orchestrator_inter_step_delta` and rendered above the
   * matching step section in the live transcript.
   */
  interStepTextByStep: Record<number, string>
  /**
   * Map of in-flight `wait_for_ingest_job` tool calls keyed by tool-use
   * id. Drives the "Ingesting… (m:ss)" status line — non-empty means at
   * least one ingest job is being awaited server-side; the indicator
   * uses the earliest `startedAtMs` so the displayed elapsed time
   * tracks the longest-running wait.
   */
  inFlightIngestWaits: Record<string, InFlightIngestWait>
  /**
   * `true` when one or more `propose_cypher_writes` proposals are
   * awaiting user accept/reject. Flips the status line to
   * "Awaiting your decision…" via {@link deriveChatStatus}.
   */
  hasInFlightCypherWrites: boolean
  finalSummary: string | null
  modelFallback: ModelFallbackInfo | null
  contextCompacted?: ContextCompactedInfo | null
}

interface ChatTurnPropsPast {
  kind: 'past'
  turn: ConversationRun
}

type ChatTurnProps = (ChatTurnPropsLive | ChatTurnPropsPast) & {
  /**
   * Invoked by the "Switch to Agent mode" button an Ask-mode answer may
   * show (from `switch_to_agent_mode`, the persisted marker, or redirect
   * prose). Receives the turn's original prompt so the composer can flip
   * to Agent and immediately rerun it. Omit on surfaces where the composer
   * mode is locked (embedded Ask lanes, ingest history) — the invite is
   * then stripped without a button.
   */
  onSwitchToAgentMode?: (prompt: string) => void
  /**
   * Hide the user-prompt bubble and show a "Switched to Agent mode"
   * indicator instead. Used for the same-prompt follow-up submitted when
   * the user clicks the Ask-mode switch button — the Agent turn still
   * runs with that prompt, but repeating it in the transcript looks like
   * the user typed it again.
   */
  hidePromptAsModeSwitch?: boolean
  /**
   * Hide the Switch to Agent mode button after it has already been used
   * for this turn (the Agent follow-up is live or already in history).
   * The marker is still stripped from the answer.
   */
  hideAgentModeSwitch?: boolean
  /** Inline artifact renders are restricted to the full chat page. */
  layout?: 'rail' | 'page'
  /** Artifact ids this turn owns for inline preview. */
  previewArtifactIds?: ReadonlySet<string>
  conversationId?: string
  attachments?: readonly ConversationAttachment[]
  /**
   * Copies this settled turn and everything before it into a new chat.
   * Omit on surfaces that do not own a conversation (ingest history).
   * Live turns never render the control.
   */
  onForkFromHere?: () => void
  /** Disables the fork control while a copy is already in flight. */
  forkInFlight?: boolean
}

/**
 * Memoized: past turns receive an Immer-stable `turn` object, so the
 * per-token SSE dispatches that re-render the sidebar skip re-rendering
 * every historical turn (each of which would otherwise re-run its full
 * markdown + CoT transcript render). The live turn's props change with
 * each delta, so it still re-renders as content streams in.
 */
export const ChatTurn = memo(function ChatTurn(props: ChatTurnProps) {
  const data = props.kind === 'live' ? extractLive(props) : extractPast(props.turn)
  const backgroundWakeup = parseChatWakeupInstruction(data.prompt)
  const fastPathStepIndex = singleDirectQueryStepIndex(data.plan)
  const pendingFastPathAnswer =
    fastPathStepIndex === null
      ? null
      : extractStreamingAnswer(data.pendingProgressByStep[fastPathStepIndex] ?? '')
  // Always strip answer envelopes — ingest Ask used to leak `<answer_start>`
  // because its synthetic plan step is `ingest_query_agent`, not `query_agent`.
  const rawDisplayedAnswer =
    backgroundWakeup?.kind === 'follow_up' || backgroundWakeup?.kind === 'bulk'
      ? null
      : data.finalSummary
        ? stripAnswerEnvelope(data.finalSummary)
        : pendingFastPathAnswer
  // Ask-mode answers may invite Agent mode (tool, marker, or redirect
  // prose); strip the marker from the rendered markdown and surface a
  // button instead.
  const answerExtraction =
    rawDisplayedAnswer !== null ? extractAgentModeSwitch(rawDisplayedAnswer) : null
  const displayedAnswer = answerExtraction?.answer || null
  const transcriptPending =
    fastPathStepIndex !== null && pendingFastPathAnswer !== null
      ? omitStep(data.pendingProgressByStep, fastPathStepIndex)
      : data.pendingProgressByStep
  const transcriptSegments =
    fastPathStepIndex === null
      ? data.liveSegmentsByStep
      : removeFastPathAnswerSegments(data.liveSegmentsByStep, fastPathStepIndex, data.finalSummary)
  const flatCot = data.plan.length === 1 && data.plan[0].intended_agent === 'ingest_query_agent'
  // While the ingest agent is waiting on the completion callback for one or
  // more jobs, override the generic "Thinking…" / verb line with a live
  // "Ingesting… (m:ss)" indicator. Only the live ChatTurn opts in — past
  // turns carry no in-flight waits.
  const ingestWaits =
    props.kind === 'live' && Object.keys(props.inFlightIngestWaits).length > 0
      ? props.inFlightIngestWaits
      : null
  const ingestStatusLine = useIngestWaitStatusLine(ingestWaits)
  // Rotate through THINKING_VERBS while the status is `thinking`. Hook
  // is unconditional (called every render); the `active` arg gates the
  // interval so it only ticks when needed.
  const thinkingVerb = useRotatingThinkingVerb(data.status?.kind === 'thinking')
  const derivedStatusLine = data.status ? statusLineFor(data.status, thinkingVerb) : null
  const statusLine = ingestStatusLine ?? derivedStatusLine
  const debugMode = useDebugMode()
  // Live turns start open in debug so the in-flight log is watchable.
  // Finished turns remount as `past` and stay closed — expanding on
  // completion used to dump the whole log under the answer.
  const [expanded, setExpanded] = useState(debugMode && props.kind === 'live')
  const [agentModeSwitchUsed, setAgentModeSwitchUsed] = useState(false)
  const answerRef = useRef<HTMLDivElement>(null)
  const switchFromTool = data.toolCalls.some((tc) => tc.name === SWITCH_TO_AGENT_MODE_TOOL_NAME)
  // A wake-up's prompt is the system wake-up instruction, not something the
  // user can resend in Agent mode.
  const showAgentModeSwitch =
    backgroundWakeup === null &&
    displayedAnswer !== null &&
    (answerExtraction?.switchToAgentMode === true || switchFromTool) &&
    props.onSwitchToAgentMode !== undefined &&
    props.hideAgentModeSwitch !== true &&
    !agentModeSwitchUsed
  // The plan + tool-calls accordion is a debug-only inspection surface;
  // normal users only see the CoT transcript above + the final answer.
  const hasPlanContent = debugMode && (data.plan.length > 0 || data.toolCalls.length > 0)
  const compactionDebugLabel =
    debugMode && data.compactionForDebug
      ? formatDebugCompactionNotice(data.compactionForDebug)
      : null
  // Visual hierarchy: when a CoT transcript renders above, separate the
  // final answer with a top border so it reads as the deliberate
  // landing zone rather than another paragraph of narration.
  //
  // Direct Ask / ingest-Q&A synthesizes a single plan step even when the
  // model answers on turn 1 with no tools. That step is only an internal
  // fast-path target — don't render "Looked into this" / "QUERY · Hello"
  // chrome when there is nothing to disclose.
  const hasVisibleTools = data.toolCalls.some((tc) => !isIgnoredReasoningTool(tc.name))
  const hasCotLeftover = hasTranscriptBody(
    transcriptSegments,
    transcriptPending,
    data.interStepTextByStep
  )
  const hideEmptyDirectQueryChrome =
    singleDirectQueryStepIndex(data.plan) !== null && !hasVisibleTools && !hasCotLeftover
  const hasTranscriptAbove = data.plan.length > 0 && !hideEmptyDirectQueryChrome
  const isComplete =
    props.kind === 'past' || (data.finalSummary !== null && data.finalSummary.length > 0)
  const pendingRaw =
    fastPathStepIndex !== null ? (data.pendingProgressByStep[fastPathStepIndex] ?? '') : ''
  // Streamdown's streaming/animating mode otherwise keeps running until the
  // SSE connection closes — persist + finalize after the last token.
  const answerStillStreaming =
    props.kind === 'live' &&
    !isComplete &&
    (fastPathStepIndex === null || !hasCompleteAnswerEnvelope(pendingRaw))
  const mountedAtRef = useRef(Date.now())
  const submittedAt =
    props.kind === 'past'
      ? parseSubmittedAt(props.turn.started_at)
      : new Date(mountedAtRef.current).toISOString()
  const durationSeconds =
    props.kind === 'past'
      ? elapsedSecondsBetween(props.turn.started_at, props.turn.finished_at)
      : isComplete
        ? Math.max(0, Math.floor((Date.now() - mountedAtRef.current) / 1000))
        : null
  const reasoningSummary = isComplete
    ? formatReasoningSummary(countReasoningLookups(data.toolCalls), durationSeconds)
    : statusLine
  const previewArtifacts =
    props.layout === 'page' && props.previewArtifactIds
      ? data.chatArtifacts.filter((artifact) => props.previewArtifactIds?.has(artifact.id))
      : []
  const previewedArtifactIds = new Set(previewArtifacts.map((artifact) => artifact.id))
  const artifactLinks = chatArtifactEntityLinks(
    data.chatArtifacts.filter((artifact) => !previewedArtifactIds.has(artifact.id))
  )
  const entityLinkGroups =
    artifactLinks.length > 0 ? [...data.entityLinkGroups, artifactLinks] : data.entityLinkGroups

  // Synthetic-resume turns carry a structured `[user decisions on
  // proposal plan-…]` instruction that's noisy when rendered as a raw
  // chat bubble (see screenshot in the original bug). Detect those and
  // swap in a compact summary bubble; everything else falls through to
  // the standard prompt rendering.
  const resumeInstruction = parseResumeInstruction(data.prompt)

  return (
    <div className="flex flex-col gap-2">
      {resumeInstruction ? (
        // A settled-decisions turn is a system event, not something the user
        // typed — render it full-width and neutral instead of in the
        // right-aligned user-message slot.
        <ResumeDecisionsBubble parsed={resumeInstruction} />
      ) : backgroundWakeup ? (
        backgroundWakeup.kind === 'follow_up' && backgroundWakeup.outcome ? (
          <FollowUpOutcomeCard outcome={backgroundWakeup.outcome} />
        ) : backgroundWakeup.kind === 'bulk' && backgroundWakeup.bulkOutcome ? (
          <Suspense fallback={null}>
            <BulkAgentBatchOutcomeCard outcome={backgroundWakeup.bulkOutcome} />
          </Suspense>
        ) : (
          <BackgroundAnalysisCompleteIndicator runId={backgroundWakeup.runId} />
        )
      ) : props.hidePromptAsModeSwitch ? (
        <SwitchedToAgentModeIndicator />
      ) : (
        <UserPromptBubble prompt={data.prompt} submittedAt={submittedAt} />
      )}

      <div className="flex flex-col gap-2">
        {hasTranscriptAbove && reasoningSummary ? (
          <ReasoningDisclosure
            summary={reasoningSummary}
            streaming={!isComplete}
            defaultOpen={debugMode || props.kind === 'live'}
          >
            <LiveTranscript
              plan={data.plan}
              stepResults={data.stepResults}
              segmentsByStep={transcriptSegments}
              pendingByStep={transcriptPending}
              interStepTextByStep={data.interStepTextByStep}
              isComplete={isComplete}
              variant={flatCot ? 'flat' : 'steps'}
              revealDetails
            />
          </ReasoningDisclosure>
        ) : statusLine && displayedAnswer === null ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="size-1.5 animate-pulse rounded-full bg-primary" />
            <span>{statusLine}</span>
          </div>
        ) : null}

        {data.modelFallback?.notify === true ? (
          <div className="flex items-start gap-2 rounded-md border border-border/70 bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
            <Sparkles className="mt-0.5 size-3.5 shrink-0" />
            <span>{formatModelFallbackNotice(data.modelFallback)}</span>
          </div>
        ) : null}

        {data.contextCompacted && compactionDebugLabel === null ? (
          <div
            className="flex items-start gap-2 rounded-md border border-border/70 bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground"
            data-testid="context-compacted-notice"
          >
            <Sparkles className="mt-0.5 size-3.5 shrink-0" />
            <span>{formatContextCompactedNotice(data.contextCompacted)}</span>
          </div>
        ) : null}

        {displayedAnswer ? (
          <div
            className={cn(
              'flex min-w-0 flex-col',
              hasTranscriptAbove && 'mt-1 border-t border-border/60 pt-2'
            )}
          >
            <div ref={answerRef} className="chat-markdown sidebar-chat min-w-0 text-foreground">
              <ChatMarkdown
                isStreaming={answerStillStreaming}
                conversationId={props.conversationId}
                figures={props.attachments}
              >
                {displayedAnswer}
              </ChatMarkdown>
            </div>
            {showAgentModeSwitch ? (
              <div className="mt-3">
                <Button
                  type="button"
                  className="h-10 w-full gap-2"
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    setAgentModeSwitchUsed(true)
                    props.onSwitchToAgentMode?.(typeof data.prompt === 'string' ? data.prompt : '')
                  }}
                >
                  <Bot className="size-4" />
                  Switch to Agent mode
                </Button>
              </div>
            ) : null}
            <div className="mt-1 flex justify-end">
              {props.kind === 'past' && props.onForkFromHere ? (
                <ForkFromHereButton onFork={props.onForkFromHere} disabled={props.forkInFlight} />
              ) : null}
              <CopyButton
                text={displayedAnswer}
                source={() => answerRef.current}
                label="Copy response"
              />
            </div>
          </div>
        ) : props.kind === 'past' && props.onForkFromHere ? (
          <div className="mt-1 flex justify-end">
            <ForkFromHereButton onFork={props.onForkFromHere} disabled={props.forkInFlight} />
          </div>
        ) : null}

        {data.error ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-destructive">
            <AlertTriangle className="mt-0.5 size-3 shrink-0" />
            <span className="break-words">{data.error}</span>
          </div>
        ) : null}

        {data.stopped ? <div className="text-11 text-muted-foreground/80">Stopped</div> : null}

        {data.ingestSubgraphs.length > 0 ? (
          <IngestSubgraphGallery previews={data.ingestSubgraphs} />
        ) : null}

        {previewArtifacts.map((artifact) => (
          <ArtifactPreviewCard
            key={artifact.id}
            artifactId={artifact.id}
            title={artifact.title}
            kind={artifact.kind}
            fetchMode={artifact.fetchMode}
          />
        ))}

        {entityLinkGroups.length > 0 ? (
          <div className="flex flex-col gap-0.5">
            {entityLinkGroups.map((group) => (
              <EntityLinksLine key={group[0].kind} links={group} />
            ))}
          </div>
        ) : null}

        {hasPlanContent ? (
          <Collapsible open={expanded} onOpenChange={setExpanded} className="flex flex-col gap-1">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="inline-flex w-fit cursor-pointer items-center gap-1 text-11 text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronRight
                  className={cn(
                    'size-3 transition-transform duration-200 ease-out motion-reduce:transition-none',
                    expanded && 'rotate-90'
                  )}
                />
                <span>
                  {data.plan.length} task{data.plan.length === 1 ? '' : 's'}
                  {data.toolCalls.length > 0
                    ? ` · ${data.toolCalls.length} tool call${data.toolCalls.length === 1 ? '' : 's'}` +
                      ` · ${batchHeadline(data.toolCalls)}`
                    : ''}
                  {compactionDebugLabel ? (
                    <span data-testid="debug-compaction-notice">{` · ${compactionDebugLabel}`}</span>
                  ) : null}
                </span>
              </button>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <div className="flex min-w-0 flex-col gap-0.5 rounded-md border bg-muted/40 p-1.5">
                {data.plan.map((step) => {
                  const result = data.stepResults[step.index]
                  const stepToolCalls = data.toolCalls.filter(
                    (tc) => tc.parentStepIndex === step.index
                  )
                  const status: StepStatus = result?.completed
                    ? 'done'
                    : result
                      ? 'running'
                      : 'pending'
                  return (
                    <CompactStepRow
                      key={step.index}
                      index={step.index}
                      agent={step.intended_agent}
                      description={step.description}
                      toolCalls={stepToolCalls}
                      status={status}
                    />
                  )
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        ) : null}

        {compactionDebugLabel && !hasPlanContent ? (
          <div className="text-11 text-muted-foreground" data-testid="debug-compaction-notice">
            {compactionDebugLabel}
          </div>
        ) : null}
      </div>
    </div>
  )
})

function BackgroundAnalysisCompleteIndicator({ runId }: { runId: string }) {
  return (
    <div className="flex flex-col gap-1">
      <div
        className="flex items-center gap-2 py-1"
        role="status"
        aria-label="Background analysis completed"
      >
        <div className="h-px min-w-0 flex-1 bg-border" />
        <span className="inline-flex shrink-0 items-center gap-1.5 text-11 font-medium text-muted-foreground">
          <Bot className="size-3" aria-hidden />
          Background analysis completed
        </span>
        <div className="h-px min-w-0 flex-1 bg-border" />
      </div>
      <CompletedCustomAgentRunLinks runId={runId} />
    </div>
  )
}

function FollowUpOutcomeCard({ outcome }: { outcome: CustomAgentFollowUpOutcome }) {
  const title =
    outcome.state === 'completed'
      ? 'Follow-up completed'
      : outcome.state === 'cancelled'
        ? 'Follow-up cancelled'
        : 'Follow-up failed'
  return (
    <div
      className="rounded-lg border border-border bg-muted/30 p-3"
      role="status"
      aria-label={title}
    >
      <div className="flex items-start gap-2">
        <Bot className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{title}</div>
          <div className="text-xs text-muted-foreground">{outcome.agentName}</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {describeCustomAgentFollowUpOutcome(outcome).map(({ key, label }) => (
              <span
                key={key}
                className="rounded-full border border-border bg-background px-2 py-0.5 text-11 font-medium text-foreground"
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-2">
        <CompletedCustomAgentRunLinks runId={outcome.parentRunId} />
      </div>
    </div>
  )
}

function SwitchedToAgentModeIndicator() {
  return (
    <div className="flex items-center gap-2 py-1" role="status" aria-label="Switched to Agent mode">
      <div className="h-px min-w-0 flex-1 bg-border" />
      <span className="inline-flex shrink-0 items-center gap-1.5 text-11 font-medium text-muted-foreground">
        <Bot className="size-3" aria-hidden />
        Switched to Agent mode
      </span>
      <div className="h-px min-w-0 flex-1 bg-border" />
    </div>
  )
}

function parseSubmittedAt(iso: string | null | undefined): string | null {
  if (!iso) return null
  return Number.isFinite(Date.parse(iso)) ? iso : null
}

/**
 * Select the whole user prompt so a double-click copies the message,
 * not just the word under the cursor.
 */
function selectElementContents(el: HTMLElement) {
  const selection = window.getSelection()
  if (!selection) return
  const range = document.createRange()
  range.selectNodeContents(el)
  selection.removeAllRanges()
  selection.addRange(range)
}

/**
 * Right-aligned user prompt. Hovering reveals the sent time underneath;
 * double-clicking selects the whole message. The prompt is a selectable
 * text node, not a button — click-to-toggle stole the copy gesture.
 */
function UserPromptBubble({ prompt, submittedAt }: { prompt: string; submittedAt: string | null }) {
  const bubbleClass =
    'max-w-full cursor-text select-text rounded-2xl rounded-br-md bg-primary/10 px-3 py-2 break-words text-13 text-foreground'
  // A message sent from a task/problem handoff carries a one-line marker
  // naming the item; render it as the chip it was in the composer.
  const { chip, text } = splitHandoffChip(prompt)

  return (
    <div className="flex justify-end">
      {/* Sized explicitly rather than inherited: the answer body is pinned
          to 0.8125rem by `.chat-markdown`, so an inherited bubble read larger
          than the reply it belongs to — 0.9375rem on the page, ambient in the
          rail. */}
      <div className="group flex max-w-[85%] flex-col items-end gap-0.5">
        {chip ? (
          <span
            className={cn(
              'mb-0.5 inline-flex max-w-full items-center rounded-full border px-2.5 py-0.5 text-11 font-medium leading-snug',
              chip.tone === 'reject'
                ? 'border-st-down/35 bg-st-down-bg text-st-down'
                : 'border-line bg-bg-sunk text-ink-2'
            )}
          >
            <span className="truncate">{handoffChipText(chip)}</span>
          </span>
        ) : null}
        <div
          onDoubleClick={(event) => {
            event.preventDefault()
            selectElementContents(event.currentTarget)
          }}
          className={bubbleClass}
        >
          {text}
        </div>
        {submittedAt ? (
          <time
            dateTime={submittedAt}
            aria-label="Sent time"
            onDoubleClick={(event) => {
              event.preventDefault()
              selectElementContents(event.currentTarget)
            }}
            className={cn(
              'cursor-text px-1 text-10 leading-none tabular-nums text-muted-foreground/70 transition-opacity',
              // Hidden stamps stay unselectable so a message drag-copy does
              // not pick up invisible time. Once revealed, the whole label
              // is selectable so it can be copied on its own.
              'select-all opacity-100',
              '[@media(hover:hover)]:select-none [@media(hover:hover)]:opacity-0',
              '[@media(hover:hover)]:group-hover:select-all [@media(hover:hover)]:group-hover:opacity-100'
            )}
            title={new Date(submittedAt).toLocaleString()}
          >
            {formatCompactLocalTime(submittedAt)}
          </time>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Collapsed reasoning line with the turn's transcript underneath.
 *
 * The live turn stays open for its whole run — through the plan, the
 * subagent narration, and the final answer streaming in — and only
 * collapses when the run ends and the finished turn takes its place.
 * Sending the next message mounts a fresh live turn, which opens again.
 * A user toggle wins until the open/closed default itself changes.
 */
function ReasoningDisclosure({
  summary,
  streaming,
  defaultOpen,
  children
}: {
  summary: string
  streaming: boolean
  defaultOpen: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)

  useEffect(() => {
    setOpen(defaultOpen)
  }, [defaultOpen])

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full cursor-pointer items-center gap-2 rounded-md px-0.5 py-1 text-left text-xs text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
        >
          {streaming ? (
            <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-primary" />
          ) : (
            <Sparkles className="size-3.5 shrink-0 text-primary" />
          )}
          <span className="min-w-0 flex-1">{summary}</span>
          <ChevronDown
            className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-180')}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-1">{children}</CollapsibleContent>
    </Collapsible>
  )
}

/**
 * Batching half of the debug accordion label. Surfaces how many turns the
 * tools were spread over so a one-tool-per-turn staircase is visible without
 * expanding anything.
 */
function batchHeadline(calls: SidebarToolCallEntry[]): string {
  const { turns } = summarizeToolBatches(calls)
  return `${turns} turn${turns === 1 ? '' : 's'}`
}

type StepStatus = 'pending' | 'running' | 'done'

interface CompactStepRowProps {
  index: number
  agent: string
  description: string
  toolCalls: SidebarToolCallEntry[]
  status: StepStatus
}

function CompactStepRow({ index, agent, description, toolCalls, status }: CompactStepRowProps) {
  const debugMode = useDebugMode()
  const [expanded, setExpanded] = useState(debugMode)
  const canExpand = debugMode && toolCalls.length > 0
  const icon = status === 'done' ? '✓' : status === 'running' ? '●' : '○'
  const iconColor =
    status === 'done'
      ? 'text-st-run'
      : status === 'running'
        ? 'animate-pulse text-primary'
        : 'text-muted-foreground'

  const header = (
    <>
      <div className="flex shrink-0 items-center gap-1">
        {canExpand ? (
          <ChevronRight
            className={cn(
              'size-3 text-muted-foreground transition-transform duration-200 ease-out motion-reduce:transition-none',
              expanded && 'rotate-90'
            )}
          />
        ) : (
          <span className="inline-block size-3" />
        )}
        <span className="font-mono text-muted-foreground">{index + 1}.</span>
      </div>
      <span className="min-w-0 flex-1 break-words text-foreground">{description}</span>
      <div className="mt-0.5 flex shrink-0 items-start gap-1.5 leading-none">
        <span className="rounded-sm bg-muted px-1.5 py-0.5 text-10 tracking-wide text-muted-foreground uppercase">
          {agentLabel(agent)}
        </span>
        <div className="flex flex-col items-center gap-0.5">
          <span className={cn('inline-block w-3 text-center', iconColor)}>{icon}</span>
          {toolCalls.length > 0 ? (
            <span className="text-10 tabular-nums text-muted-foreground">{toolCalls.length}</span>
          ) : null}
        </div>
      </div>
    </>
  )

  if (!canExpand) {
    return <div className="flex min-w-0 items-start gap-1.5 px-1 py-0.5 text-11">{header}</div>
  }

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded} className="flex min-w-0 flex-col">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex min-w-0 cursor-pointer items-start gap-1.5 rounded px-1 py-0.5 text-left text-11 hover:bg-accent"
        >
          {header}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1">
          <SidebarToolCallList calls={toolCalls} />
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

interface NormalizedTurn {
  prompt: string
  plan: AgentOrchestratorPlanStep[]
  stepResults: Record<number, AgentOrchestratorStepResult>
  toolCalls: SidebarToolCallEntry[]
  finalSummary: string | null
  error: string | null
  /** The user stopped this turn; whatever it got through is what's here. */
  stopped: boolean
  /**
   * Inline status (derived live; rendered by the component which threads
   * in the rotating-thinking-verb hook). `null` for past turns — they
   * don't surface a status line.
   */
  status: ChatStatus | null
  /** Per-kind buckets of entities the subagents created/touched. */
  entityLinkGroups: EntityLink[][]
  /** Conversation-scoped artifacts created or revised by this turn. */
  chatArtifacts: ChatArtifactRef[]
  /** Inline SVG previews from any ingest_agent step's draw_cypher_subgraph calls (and legacy draw_ingest_subgraph in older history). */
  ingestSubgraphs: IngestSubgraphPreview[]
  /** Per-step committed transcript segments (live turns only; empty for past turns). */
  liveSegmentsByStep: Record<number, LiveSegment[]>
  /** Per-step pending in-flight progress text (live turns only). */
  pendingProgressByStep: Record<number, string>
  /** Per-step orchestrator inter-step narration (live turns only). */
  interStepTextByStep: Record<number, string>
  modelFallback: ModelFallbackInfo | null
  contextCompacted: ContextCompactedInfo | null
  /** Live event, or a persisted compaction block. Rendered only in debug mode. */
  compactionForDebug: ContextCompactedInfo | null
}

function extractLive(props: ChatTurnPropsLive): NormalizedTurn {
  const plan = props.plan ?? []
  const textOnly = isTextOnlyPlan(plan)
  const finalSummary = pickDisplaySummary(plan, props.stepResults, props.finalSummary)

  const status = deriveChatStatus({
    plan: props.plan,
    stepResults: props.stepResults,
    toolCalls: props.toolCalls,
    pendingProgressByStep: props.pendingProgressByStep,
    hasInFlightCypherWrites: props.hasInFlightCypherWrites,
    finalSummary
  })

  return {
    prompt: props.prompt,
    plan: textOnly ? [] : plan,
    stepResults: textOnly ? {} : props.stepResults,
    toolCalls: textOnly ? [] : liveToolCallsToEntries(props.toolCalls, plan),
    finalSummary,
    error: null,
    stopped: false,
    status,
    entityLinkGroups: textOnly ? [] : aggregateStepEntityLinks(Object.values(props.stepResults)),
    chatArtifacts: textOnly ? [] : collectChatArtifactRefs(props.toolCalls),
    ingestSubgraphs: textOnly ? [] : aggregateStepIngestSubgraphs(Object.values(props.stepResults)),
    liveSegmentsByStep: textOnly ? {} : props.liveSegmentsByStep,
    pendingProgressByStep: textOnly ? {} : props.pendingProgressByStep,
    interStepTextByStep: textOnly ? {} : props.interStepTextByStep,
    modelFallback: props.modelFallback,
    contextCompacted: props.contextCompacted ?? null,
    compactionForDebug: props.contextCompacted ?? null
  }
}

/**
 * Build the chat-level "Ingesting… (m:ss)" status line while one or more
 * `wait_for_ingest_job` tool calls are in flight. Returns `null` when no
 * waits are active so the caller can fall back to the generic
 * `extractLive` status line.
 *
 * Drives a 1 Hz `setInterval` only while `waits` is non-null, so when the
 * ingest finishes (or no ingest is in flight) the timer is torn down and
 * the component stops re-rendering. The earliest `startedAtMs` across
 * concurrent waits drives the displayed elapsed time so the user sees
 * the longest-running ingest.
 */
function useIngestWaitStatusLine(waits: Record<string, InFlightIngestWait> | null): string | null {
  const [nowMs, setNowMs] = useState<number>(() => Date.now())

  useEffect(() => {
    if (waits === null) return
    setNowMs(Date.now())
    const id = window.setInterval(() => {
      setNowMs(Date.now())
    }, 1000)
    return () => {
      window.clearInterval(id)
    }
  }, [waits])

  if (waits === null) return null
  const entries = Object.values(waits)
  if (entries.length === 0) return null
  const earliestStart = entries.reduce(
    (min, w) => Math.min(min, w.startedAtMs),
    Number.POSITIVE_INFINITY
  )
  const elapsedSec = Math.max(0, Math.floor((nowMs - earliestStart) / 1000))
  const formatted = formatElapsed(elapsedSec)
  if (entries.length === 1) {
    return `Ingesting… (${formatted})`
  }
  return `Ingesting ${entries.length} jobs… (${formatted})`
}

function formatElapsed(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

function liveToolCallsToEntries(
  calls: LiveOrchestratorToolCall[],
  plan: AgentOrchestratorPlanStep[]
): SidebarToolCallEntry[] {
  return calls.map((call) => ({
    id: call.id,
    name: call.name,
    input: call.input,
    output: call.output,
    parentStepIndex: call.parentStepIndex,
    agentKind: call.agentKind ?? plan[call.parentStepIndex]?.intended_agent ?? '',
    depth: 0,
    status: call.status,
    batchId: call.batchId,
    durationMs: call.durationMs ?? null,
    ...(call.startedAt !== undefined ? { startedAt: call.startedAt } : {}),
    ...(call.turnTiming !== undefined ? { turnTiming: call.turnTiming } : {})
  }))
}

function extractPast(turn: ConversationRun): NormalizedTurn {
  const obj =
    turn.raw_response !== null && typeof turn.raw_response === 'object'
      ? (turn.raw_response as {
          plan?: unknown
          step_results?: unknown
          final_summary?: unknown
          tool_calls?: unknown
        })
      : null
  const persistedPlan: AgentOrchestratorPlanStep[] =
    obj && Array.isArray(obj.plan) ? (obj.plan as AgentOrchestratorPlanStep[]) : []
  // A harness run with no plan of its own (config builder, ingest lead, …)
  // is rendered as the single step it is; see `synthesizeFlatRunPlan`.
  const flatRun = persistedPlan.length === 0 ? synthesizeFlatRunPlan(turn) : null
  const plan = flatRun ? [flatRun.step] : persistedPlan
  const stepResultsArr: AgentOrchestratorStepResult[] =
    obj && Array.isArray(obj.step_results)
      ? (obj.step_results as AgentOrchestratorStepResult[])
      : []
  const rawFinalSummary =
    obj && typeof obj.final_summary === 'string' && obj.final_summary.length > 0
      ? obj.final_summary
      : null

  const stepResults: Record<number, AgentOrchestratorStepResult> = {}
  for (const step of stepResultsArr) {
    stepResults[step.stepIndex] = step
  }
  if (flatRun?.result) stepResults[flatRun.step.index] = flatRun.result
  const toolCalls = buildPastTurnToolCalls(turn, plan, obj?.tool_calls)

  const textOnly = isTextOnlyPlan(plan)
  const finalSummary = pickDisplaySummary(plan, stepResults, rawFinalSummary)
  const hasPartialContent =
    (finalSummary !== null && finalSummary.length > 0) || plan.length > 0 || toolCalls.length > 0
  const cot = pastTurnCot(turn)
  if (flatRun && flatRun.segments.length > 0 && !cot.liveSegmentsByStep[flatRun.step.index]) {
    cot.liveSegmentsByStep[flatRun.step.index] = flatRun.segments
  }

  return {
    prompt: turn.prompt,
    plan: textOnly ? [] : plan,
    stepResults: textOnly ? {} : stepResults,
    toolCalls: textOnly ? [] : toolCalls,
    finalSummary,
    error:
      turn.status === 'aborted' && hasPartialContent
        ? null
        : turn.error && turn.error.length > 0
          ? turn.error
          : null,
    stopped: turn.status === 'aborted',
    status: null,
    entityLinkGroups: textOnly ? [] : aggregateStepEntityLinks(stepResultsArr),
    chatArtifacts: textOnly ? [] : collectChatArtifactRefs(toolCalls),
    ingestSubgraphs: textOnly ? [] : aggregateStepIngestSubgraphs(stepResultsArr),
    liveSegmentsByStep: textOnly ? {} : cot.liveSegmentsByStep,
    pendingProgressByStep: {},
    interStepTextByStep: textOnly ? {} : cot.interStepTextByStep,
    modelFallback: turn.modelFallback ?? null,
    contextCompacted: turn.contextCompacted ?? null,
    compactionForDebug: compactionInfoFromRun(turn)
  }
}

function isTextOnlyPlan(plan: AgentOrchestratorPlanStep[]): plan is [AgentOrchestratorPlanStep] {
  return plan.length === 1 && plan[0].intended_agent === 'text'
}

/**
 * Single-step direct query paths (`query_agent_only` / `ingest_query_agent_only`)
 * synthesize one plan step. Treat both as the fast-path answer target so
 * `<answer_start>` / `</answer_end>` route into the answer body instead of CoT.
 */
function singleDirectQueryStepIndex(plan: AgentOrchestratorPlanStep[]): number | null {
  if (plan.length !== 1) return null
  const agent = plan[0].intended_agent
  if (agent === 'query_agent' || agent === 'ingest_query_agent') return plan[0].index
  return null
}

function hasTranscriptBody(
  segmentsByStep: Record<number, LiveSegment[]>,
  pendingByStep: Record<number, string>,
  interStepTextByStep: Record<number, string>
): boolean {
  for (const segments of Object.values(segmentsByStep)) {
    if (segments.some((segment) => segment.text.trim().length > 0)) return true
  }
  for (const pending of Object.values(pendingByStep)) {
    if (pending.trim().length > 0) return true
  }
  for (const text of Object.values(interStepTextByStep)) {
    if (text.trim().length > 0) return true
  }
  return false
}

function omitStep<T>(record: Record<number, T>, stepIndex: number): Record<number, T> {
  if (!(stepIndex in record)) return record
  const copy = { ...record }
  delete copy[stepIndex]
  return copy
}

function removeFastPathAnswerSegments(
  segmentsByStep: Record<number, LiveSegment[]>,
  stepIndex: number,
  finalSummary: string | null
): Record<number, LiveSegment[]> {
  const segments = segmentsByStep[stepIndex]
  if (!segments || segments.length === 0) return segmentsByStep

  const normalizedFinal = finalSummary ? stripAnswerEnvelope(finalSummary) : null
  const filtered = segments.filter((segment) => {
    if (extractStreamingAnswer(segment.text) !== null) return false
    return normalizedFinal === null || stripAnswerEnvelope(segment.text) !== normalizedFinal
  })
  if (filtered.length === segments.length) return segmentsByStep

  return { ...segmentsByStep, [stepIndex]: filtered }
}

function pickDisplaySummary(
  plan: AgentOrchestratorPlanStep[],
  stepResults: Record<number, AgentOrchestratorStepResult>,
  orchestratorSummary: string | null
): string | null {
  if (isTextOnlyPlan(plan)) {
    const textReply = stepResults[plan[0].index]?.summary ?? ''
    return textReply.length > 0 ? textReply : null
  }
  return orchestratorSummary
}

/**
 * Single outer accordion wrapping all of a turn's per-group ingest
 * subgraph renders. The outer toggle expands/collapses the whole gallery;
 * inside, each diagram is its own independently-toggleable row. The
 * first diagram is open by default so the user sees an immediate
 * preview, while the rest stay collapsed to keep the chat compact when
 * an ingest produces 6+ groups.
 *
 * Job grouping in the heading: when every preview shares the same jobId
 * (the common case), the header shows the short job suffix. With mixed
 * jobs in a single turn (re-render of multiple jobs in one prompt), the
 * job badge is dropped.
 */
function IngestSubgraphGallery({ previews }: { previews: IngestSubgraphPreview[] }) {
  const [open, setOpen] = useState(true)
  const count = previews.length
  const uniqueJobIds = Array.from(
    new Set(previews.map((p) => p.jobId).filter((id): id is string => id !== null))
  )
  const sharedJobId = uniqueJobIds.length === 1 ? uniqueJobIds[0] : null
  return (
    <div className="flex min-w-0 flex-col rounded-md border border-line bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center gap-1.5 px-2 py-1.5 text-left text-11 text-muted-foreground hover:bg-muted/60"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        <span className="font-medium text-foreground">
          {count} ingest diagram{count === 1 ? '' : 's'}
        </span>
        {sharedJobId ? (
          <>
            <span>·</span>
            <span className="truncate font-mono">job {sharedJobId.slice(0, 8)}</span>
          </>
        ) : null}
      </button>
      {open ? (
        <div className="flex flex-col gap-1 border-t border-line p-1.5">
          {previews.map((preview, index) => (
            <IngestSubgraphCard
              key={preview.toolCallId}
              preview={preview}
              hideJobBadge={sharedJobId !== null}
              defaultOpen={index === 0}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Tailwind chunk applied to the wrapper around an inline SVG. The
 * server renders the SVG with hardcoded `width="720" height="480"`
 * attributes — those wins over CSS unless we override them. The
 * `[&>svg]:*` arbitrary-child selectors force the SVG to scale to the
 * parent's width, preserve aspect ratio via the existing viewBox, and
 * drop the scrollbars the chat sidebar used to show.
 */
const RESPONSIVE_SVG_CLASS = '[&>svg]:block [&>svg]:h-auto [&>svg]:max-w-full [&>svg]:w-full'

function IngestSubgraphCard({
  preview,
  hideJobBadge,
  defaultOpen
}: {
  preview: IngestSubgraphPreview
  hideJobBadge: boolean
  defaultOpen: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const [zoomed, setZoomed] = useState(false)
  const sanitized = sanitizeSubgraphSvg(preview.svg)
  const counts = `${preview.nodeCount} node${preview.nodeCount === 1 ? '' : 's'}, ${preview.edgeCount} edge${preview.edgeCount === 1 ? '' : 's'}`
  const jobBadge = !hideJobBadge && preview.jobId ? `job ${preview.jobId.slice(0, 8)}` : null
  return (
    <>
      <figure className="flex min-w-0 flex-col rounded border border-line bg-bg-elev">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full cursor-pointer items-center gap-1.5 px-1.5 py-1 text-left text-11 text-muted-foreground hover:bg-muted/60"
          aria-expanded={open}
        >
          {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          <span className="font-medium text-foreground">{preview.title}</span>
          <span>·</span>
          <span>{counts}</span>
          {jobBadge ? (
            <>
              <span>·</span>
              <span className="truncate font-mono">{jobBadge}</span>
            </>
          ) : null}
        </button>
        {open ? (
          <div className="flex min-w-0 flex-col gap-1 border-t border-line p-1.5">
            <div className="group relative min-w-0">
              <button
                type="button"
                onClick={() => setZoomed(true)}
                aria-label={`View ${preview.title} full screen`}
                title="Click to view full screen"
                className={cn(
                  'block w-full min-w-0 cursor-zoom-in rounded bg-muted/40 p-1 hover:bg-muted/60',
                  RESPONSIVE_SVG_CLASS
                )}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: sanitized }}
              />
              <Maximize2
                aria-hidden="true"
                className="pointer-events-none absolute top-1.5 right-1.5 size-4 rounded bg-background/80 p-0.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
              />
            </div>
            {preview.note ? (
              <figcaption className="text-10 text-muted-foreground italic">
                {preview.note}
              </figcaption>
            ) : null}
          </div>
        ) : null}
      </figure>
      <Dialog open={zoomed} onOpenChange={setZoomed}>
        <DialogContent className="flex max-h-[95vh] w-[min(95vw,75rem)] max-w-[95vw] flex-col gap-3 p-4 sm:max-w-[95vw]">
          <DialogHeader className="gap-1">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Maximize2 className="size-4 text-muted-foreground" />
              <span>{preview.title}</span>
            </DialogTitle>
            <DialogDescription>
              {counts}
              {jobBadge ? ` · ${jobBadge}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div
            className={cn(
              'min-w-0 flex-1 overflow-auto rounded bg-muted/40 p-2',
              RESPONSIVE_SVG_CLASS
            )}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: sanitized }}
          />
          {preview.note ? (
            <p className="text-xs text-muted-foreground italic">{preview.note}</p>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

function EntityLinksLine({ links }: { links: EntityLink[] }) {
  if (links.length === 0) return null
  const listRoute = LIST_ROUTE_FOR[links[0].kind]
  const collapsed = listRoute !== undefined && links.length > MAX_INLINE_ENTITY_LINKS
  return (
    <div className="text-11 text-muted-foreground">
      <span>Created </span>
      {collapsed ? (
        <RouterLink to={listRoute} className="text-primary underline-offset-2 hover:underline">
          {links.length} {ENTITY_KIND_LABEL[links[0].kind]}s
        </RouterLink>
      ) : (
        links.map((link, i) => (
          <Fragment key={link.id}>
            {i > 0 ? <span>, </span> : null}
            <RouterLink to={link.href} className="text-primary underline-offset-2 hover:underline">
              {link.label}
            </RouterLink>
          </Fragment>
        ))
      )}
    </div>
  )
}

/**
 * Normalize one persisted turn's tool calls for previews and the debug task
 * list without making callers duplicate the raw-response fallback rules.
 */
export function pastTurnToolCallEntries(turn: ConversationRun): SidebarToolCallEntry[] {
  const response =
    turn.raw_response !== null && typeof turn.raw_response === 'object'
      ? (turn.raw_response as { plan?: unknown; tool_calls?: unknown })
      : null
  const plan =
    response && Array.isArray(response.plan) ? (response.plan as AgentOrchestratorPlanStep[]) : []
  return buildPastTurnToolCalls(turn, plan, response?.tool_calls)
}

/**
 * Past-turn tool calls for persisted turn extraction and the debug task list.
 *
 * Pick one source, never concatenate:
 * - While the turn is streaming, the live row already upserts each tool as
 *   SSE events arrive.
 * - After refresh / conversation refetch, the persisted message tree is the
 *   full chain (root messages for Ask/direct-dispatch, child runs for the
 *   full orchestrator).
 * - `raw_response.tool_calls` is a second copy of that same chain. Its ids
 *   match the persisted `tool_use` blocks, so use it to attach timing but
 *   never append it on top of message-derived rows. Use it as the row source
 *   only when the message tree is empty — the synthetic live→past snapshot
 *   before the server refetch lands.
 *
 * Attribution mirrors the live path (`liveToolCallsToEntries`): a tool call
 * is badged with the intended agent of the plan step it ran under, NOT with
 * the root run's `agent_kind` — the root run is always the orchestrator, so
 * using it badged every reloaded query-agent call as "orch" and made the
 * query and orchestrator agents look interleaved in the same UI.
 *
 */
export function buildPastTurnToolCalls(
  turn: ConversationRun,
  plan: AgentOrchestratorPlanStep[],
  rawToolCalls: unknown
): SidebarToolCallEntry[] {
  const out: SidebarToolCallEntry[] = []
  const seen = new Set<string>()

  const push = (entry: SidebarToolCallEntry): void => {
    if (seen.has(entry.id)) return
    seen.add(entry.id)
    out.push(entry)
  }

  const agentKindForStep = (stepIndex: number): string =>
    plan.find((step) => step.index === stepIndex)?.intended_agent ?? turn.agent_kind

  // Direct-dispatch runs synthesize a single query / ingest-query plan step
  // and persist the SUBAGENT's tool calls on the root run. Attribute those
  // to the synthesized step's agent. In full orchestrator mode the root
  // messages are the planner's own calls and keep the orchestrator kind.
  const directStepIndex = singleDirectQueryStepIndex(plan)
  const timingById = toolCallTimingById(rawToolCalls)

  for (const call of flattenMessagesToToolCalls(turn.messages)) {
    const timing = timingById.get(call.id)
    push({
      id: call.id,
      name: call.name,
      input: call.input,
      output: call.output,
      parentStepIndex: directStepIndex ?? 0,
      agentKind: directStepIndex !== null ? agentKindForStep(directStepIndex) : turn.agent_kind,
      depth: 0,
      status: call.status,
      batchId: `${turn.id}:${call.batchId}`,
      durationMs: timing?.durationMs ?? null,
      ...(timing?.startedAt !== undefined ? { startedAt: timing.startedAt } : {}),
      ...(timing?.turnTiming !== undefined ? { turnTiming: timing.turnTiming } : {})
    })
  }

  for (const child of turn.children) {
    walkRun(child, child.position, 0, out, seen)
  }

  if (out.length > 0) return out

  if (Array.isArray(rawToolCalls)) {
    for (const raw of rawToolCalls) {
      if (!raw || typeof raw !== 'object') continue
      const rec = raw as Record<string, unknown>
      const id = typeof rec.id === 'string' ? rec.id : null
      const name = typeof rec.name === 'string' ? rec.name : null
      if (!id || !name) continue
      const parentStepIndex =
        typeof rec.parentStepIndex === 'number'
          ? rec.parentStepIndex
          : typeof rec.parent_step_index === 'number'
            ? rec.parent_step_index
            : (directStepIndex ?? 0)
      const startedAt = readCallStartedAt(rec)
      const turnTiming = readCallTurnTiming(rec)
      const persistedAgentKind =
        typeof rec.agentKind === 'string'
          ? rec.agentKind
          : typeof rec.agent_kind === 'string'
            ? rec.agent_kind
            : undefined
      push({
        id,
        name,
        input: rec.input,
        output: rec.output,
        parentStepIndex,
        agentKind: persistedAgentKind ?? agentKindForStep(parentStepIndex),
        depth: 0,
        status: rec.status === 'running' ? 'running' : 'completed',
        batchId:
          typeof rec.batchId === 'string' && rec.batchId.length > 0
            ? rec.batchId
            : typeof rec.batch_id === 'string' && rec.batch_id.length > 0
              ? rec.batch_id
              : undefined,
        durationMs: readCallDurationMs(rec),
        ...(startedAt !== undefined ? { startedAt } : {}),
        ...(turnTiming !== undefined ? { turnTiming } : {})
      })
    }
  }

  return out
}

function walkRun(
  run: ConversationRun,
  parentStepIndex: number,
  depth: number,
  out: SidebarToolCallEntry[],
  seen: Set<string> = new Set()
): void {
  const flat = flattenMessagesToToolCalls(run.messages)
  const timingById = toolCallTimingById(rawToolCallsFromResponse(run.raw_response))
  for (const call of flat) {
    if (seen.has(call.id)) continue
    seen.add(call.id)
    const timing = timingById.get(call.id)
    out.push({
      id: call.id,
      name: call.name,
      input: call.input,
      output: call.output,
      parentStepIndex,
      agentKind: run.agent_kind,
      depth,
      status: call.status,
      batchId: `${run.id}:${call.batchId}`,
      durationMs: timing?.durationMs ?? null,
      ...(timing?.startedAt !== undefined ? { startedAt: timing.startedAt } : {}),
      ...(timing?.turnTiming !== undefined ? { turnTiming: timing.turnTiming } : {})
    })
  }
  for (const grandchild of run.children) {
    walkRun(grandchild, parentStepIndex, depth + 1, out, seen)
  }
}

function rawToolCallsFromResponse(rawResponse: unknown): unknown {
  if (rawResponse === null || typeof rawResponse !== 'object') return undefined
  const rec = rawResponse as Record<string, unknown>
  return rec.tool_calls ?? rec.toolCalls
}

interface PersistedCallTiming {
  durationMs: number | null
  startedAt?: number
  turnTiming?: TurnPhaseTiming
}

/**
 * Index the run's persisted tool-call records by the model's `tool_use` id
 * so message-derived rows can pick their timing back up after the turn
 * completes.
 */
function toolCallTimingById(rawToolCalls: unknown): Map<string, PersistedCallTiming> {
  const timings = new Map<string, PersistedCallTiming>()
  if (!Array.isArray(rawToolCalls)) return timings
  for (const raw of rawToolCalls) {
    if (raw === null || typeof raw !== 'object') continue
    const rec = raw as Record<string, unknown>
    const id = typeof rec.id === 'string' ? rec.id : null
    if (id === null) continue
    const durationMs = readCallDurationMs(rec)
    const startedAt = readCallStartedAt(rec)
    const turnTiming = readCallTurnTiming(rec)
    if (durationMs === null && startedAt === undefined && turnTiming === undefined) continue
    timings.set(id, {
      durationMs,
      ...(startedAt !== undefined ? { startedAt } : {}),
      ...(turnTiming !== undefined ? { turnTiming } : {})
    })
  }
  return timings
}
